export const normalizeAddressSchema = schema => {
  if (!schema) return {};

  return {
    streetOne: schema.streetOne || schema.street1,
    streetTwo: schema.streetTwo || schema.street2,
    city: schema.city,
    state: schema.state,
    postalCode: schema.postalCode || schema.zipcode,
    country: schema.country,
  };
};

const normalizeCountry = country => {
  switch (country) {
    case 'CAN':
    case 'Canada':
    case 'CA':
      return 'CA';
    case 'USA':
    case 'United States':
    case 'US':
      return 'US';
    default:
      return country;
  }
};

const US_POSTAL_CODE_REGEX = /^\d{5}(?:-\d{4})?$/;
const CA_POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const STATE_CODE_REGEX = /^[A-Za-z]{2,3}$/;

const parseUSAddress = parts => {
  const tail = parts.slice(0, -1);
  const last = tail[tail.length - 1];
  const previous = tail[tail.length - 2];
  const fallbackCity = parts[1];

  const statePostalMatch = (last || '').match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (statePostalMatch) {
    const [, state, postalCode] = statePostalMatch;
    return { city: tail[tail.length - 2] || fallbackCity, state, postalCode };
  }

  if (US_POSTAL_CODE_REGEX.test(last || '') && STATE_CODE_REGEX.test(previous || '')) {
    return { city: tail[tail.length - 3] || fallbackCity, state: previous, postalCode: last };
  }

  if (STATE_CODE_REGEX.test(last || '')) {
    return { city: tail[tail.length - 2] || fallbackCity, state: last, postalCode: undefined };
  }

  return { city: fallbackCity, state: parts[2], postalCode: undefined };
};

const parseCanadianAddress = parts => {
  const tail = parts.slice(0, -1);
  const last = tail[tail.length - 1];
  const previous = tail[tail.length - 2];
  const fallbackCity = parts[1];

  const statePostalMatch = (last || '').match(
    /^([A-Za-z]{2,3})\s+([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/i,
  );
  if (statePostalMatch) {
    const [, state, postalCode] = statePostalMatch;
    return { city: tail[tail.length - 2] || fallbackCity, state, postalCode };
  }

  if (CA_POSTAL_CODE_REGEX.test(last || '') && STATE_CODE_REGEX.test(previous || '')) {
    return { city: tail[tail.length - 3] || fallbackCity, state: previous, postalCode: last };
  }

  if (STATE_CODE_REGEX.test(last || '')) {
    return { city: tail[tail.length - 2] || fallbackCity, state: last, postalCode: undefined };
  }

  return { city: fallbackCity, state: parts[2], postalCode: undefined };
};

const parseAddress = parts => {
  const city = parts[1];
  const stateInfo = parts[2];
  const stateAndPostalCodeMatch = (stateInfo || '').match(/^([A-Za-z]{2,3})\s+(.+)$/);

  if (stateAndPostalCodeMatch) {
    const [, state, postalCode] = stateAndPostalCodeMatch;
    return { city, state, postalCode };
  }

  return { city, state: stateInfo, postalCode: undefined };
};

export const analyseAddressAutoCompleteString = addressString => {
  const parts = (addressString || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

  if (!parts.length) {
    return {
      streetOne: '',
      city: undefined,
      state: undefined,
      country: undefined,
      postalCode: undefined,
    };
  }

  const streetOne = parts[0];
  const country = normalizeCountry(parts[parts.length - 1]);
  const addressParser =
    country === 'CA' ? parseCanadianAddress : country === 'US' ? parseUSAddress : parseAddress;
  const { city, state, postalCode } = addressParser(parts);

  return { streetOne, city, state, country, postalCode };
};

/**
 * Splits an address string into its components: the street address and the postal code with country.
 *
 * @param {string} addressString - The address string to be split.
 * @returns {[string, string]} An array containing the street address and the postal code with country.
 *
 * @example
 * splitAddressString('123 Main St, NY 12345-6789 US');
 * // returns ['123 Main St, NY', 'US, 12345-6789']
 *
 * @example
 * splitAddressString('123 Main St NY US 12345-6789');
 * // returns ['123 Main St NY', 'US, 12345-6789']
 *
 * @example
 * splitAddressString('456 Elm St, A1B 2C3');
 * // returns ['456 Elm St', 'CA, A1B 2C3']
 */
export const splitAddressString = addressString => {
  const regex = /(.*?)(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d),?\s?(\w{2,3})$/;

  const match = addressString.match(regex) || [];
  let [, streetStateCountry, postalCode, , countryCode] = match;

  if (streetStateCountry && postalCode && countryCode) {
    return [streetStateCountry, `${countryCode}, ${postalCode}`];
  }

  const additionalRegex = /(.*?)(\w{2,3}),?\s?(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d)$/;
  [, streetStateCountry, countryCode, postalCode] = addressString.match(additionalRegex) || [];

  if (streetStateCountry && postalCode && countryCode) {
    return [streetStateCountry, `${countryCode}, ${postalCode}`]; // Return empty street and the matched country code with postal code
  }

  return [addressString, '']; // Fallback if no match is found
};
